import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight } from 'lucide-react';
import IndiaMap from '../../assets/careers/empower.png';

const ExploreSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const handleImageLoad = useCallback(() => {
        setImageLoaded(true);
    }, []);

    const handleImageError = useCallback(() => {
        setImageError(true);
        console.error('Failed to load India map image');
    }, []);

    return (
        <section
            ref={sectionRef}
            className="w-full bg-white pt-20 pb-8 overflow-hidden"
            aria-labelledby="growth-section-title"
            role="region"
        >
            <div className="max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-20 items-center">

                    {/* Left Content */}
                    <div className="space-y-8">
                        {/* Main heading */}
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.2s' }}
                        >
                            <h2
                                id="growth-section-title"
                                className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 leading-tight"
                            >
                                Our Commitment to <span className="text-[#F05A28]">Innovation and Excellence</span>
                            </h2>
                        </div>

                        {/* Description */}
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.4s' }}
                        >
                            <p className="text-lg text-gray-600 leading-relaxed max-w-xl mb-6">
                                At RoadExpress, we believe in fostering a culture of innovation and collaboration. Our mission is to revolutionize logistics through cutting-edge solutions and a dedicated team.
                            </p>
                        </div>
                    </div>

                    {/* Right Content - Map */}
                    <div
                        className={`relative transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[50px] opacity-0'
                            }`}
                        style={{ transitionDelay: '0.8s' }}
                    >
                        <div className="relative">
                            {!imageError ? (
                                <div className="relative">
                                    {/* Loading skeleton */}
                                    {!imageLoaded && (
                                        <div className="absolute inset-0 bg-gray-200 rounded-2xl animate-pulse"></div>
                                    )}
                                    <img
                                        src={IndiaMap}
                                        alt="RoadExpress logistics network coverage map showing service areas across 500+ cities in India including major metros, tier-2 and tier-3 cities"
                                        className={`w-full h-auto rounded-2xl transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                                            }`}
                                        onLoad={handleImageLoad}
                                        onError={handleImageError}
                                        loading="lazy"
                                        width="600"
                                        height="400"
                                    />
                                </div>
                            ) : (
                                <div className="w-full h-96 flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl shadow-lg">
                                    <div className="text-center">
                                        <div className="text-6xl mb-4">🇮🇳</div>
                                        <div className="text-xl font-semibold text-gray-800">India Coverage Network</div>
                                        <div className="text-sm text-gray-600 mt-2">Serving 500+ cities nationwide</div>
                                        <div className="text-xs text-gray-500 mt-1">From metros to tier-3 cities</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            {/* Enhanced SEO Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "RoadExpress",
                        "description": "India's leading logistics technology platform providing reliable transportation solutions across 500+ cities",
                        "foundingDate": "2018",
                        "areaServed": {
                            "@type": "Country",
                            "name": "India"
                        },
                        "serviceType": ["Logistics Services", "Transportation", "Supply Chain Management", "Freight Services"],
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.8",
                            "ratingCount": "5000",
                            "bestRating": "5",
                            "worstRating": "1"
                        },
                        "address": {
                            "@type": "PostalAddress",
                            "addressCountry": "IN"
                        },
                        "sameAs": [],
                        "knowsAbout": ["Logistics", "Transportation", "Supply Chain", "Freight Management", "Last Mile Delivery"]
                    })
                }}
            />
        </section>
    );
};

export default ExploreSection;